import {Box, Stack} from '@dropbox/dig-foundations';
import {pulseUserAtom} from 'atoms/auth';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {snackbarAtom} from 'atoms/snackbar';
import {Layout} from 'components/DSYS/Layout';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router';
import {displayName} from 'views/apps/appUtils';
import {Banner} from 'views/home/Banner';
import {isHomeDirectoryOn, isHomeDirectoryVisit} from 'views/home/homeDirectory';
import {SavedAiViewsSection} from 'views/home/sections/SavedAiViews';
import {SearchSection} from 'views/home/sections/Search';

import styles from './Dashboard.module.css';
import {logDashboardInteraction, logHomeSurfaceViewed} from './dashboardAnalytics';
import {DashboardCustomizeBar} from './DashboardCustomizeBar';
import {DashboardEmptyState} from './DashboardEmptyState';
import {DashboardGrid} from './DashboardGrid';
import {DashboardHeader} from './DashboardHeader';
import {
  DashboardColumns,
  DEFAULT_DASHBOARD_COLUMNS,
  resolveLayout,
  useDashboardLayout,
  useEffectiveColumns,
} from './dashboardLayout';
import {TileId} from './dashboardModel';
import {HomeVisitContext} from './HomeVisitContext';
import {buildLiveTileStatuses, getVisibleLiveTilesInOrder} from './liveDashboard';
import {appSlugFromTileId, toAppTileId, usePinnedApps} from './pinnedApps';
import {StickySearchBar} from './StickySearchBar';
import {isTileFeatureEnabled} from './tileCatalog';
import {useAddAppFromUrl} from './useAddAppFromUrl';
import {useDashboard} from './useDashboard';
import {useHomeAgendaLoadedLog} from './useHomeAgendaLoadedLog';
import {newHomeVisitId, readHomeViewport, useHomeVisitTracking} from './useHomeVisitTracking';
import {useRealDashboardData} from './useRealDashboardData';
import {useTilePresence} from './useTilePresence';

// Centered-column width per grid density (see Dashboard.module.css). Wider
// boards for more columns so each tile keeps a consistent width.
const COLUMN_CONTAINER_CLASS: Record<DashboardColumns, string> = {
  2: styles.containerWidth2,
  3: styles.containerWidth3,
  4: styles.containerWidth4,
};

export const Dashboard = ({customizeEnabled}: {customizeEnabled: boolean}) => {
  useDocumentTitle(t('home'));
  const pulseUser = useAtomValue(pulseUserAtom);
  const flags = useAtomValue(growthbookCacheAtom);
  const {isAppBuilderPlatformEnabled} = flags;
  // When home is also the directory, a visit redirected from /people logs its
  // search under its own source.
  const location = useLocation();
  const fromPeople = isHomeDirectoryOn(flags) && isHomeDirectoryVisit(location.state);
  const setSnackbar = useSetAtom(snackbarAtom);
  const {employee, employeeTeams} = useAtomValue(loggedInEmployeeAtom);
  const [editing, setEditing] = useState(false);

  const {state: baseState} = useDashboard();
  const state = useRealDashboardData(baseState);

  const firstName = useMemo(() => {
    const name = employee?.name ?? pulseUser?.display_name;
    return name?.split(' ')[0];
  }, [employee?.name, pulseUser?.display_name]);

  const hasTeam = useMemo(() => Boolean(employeeTeams?.some((team) => team.slug)), [employeeTeams]);

  const {remembered, isFirstEverLoad, recordStatuses} = useTilePresence(employee?.user_id);
  const {config, setOrder, hideTile, addTile, setTileSize, setColumns, resetLayout} =
    useDashboardLayout(employee?.user_id);

  const userColumns = config.columns ?? DEFAULT_DASHBOARD_COLUMNS;
  const columns = userColumns;
  const effectiveColumns = useEffectiveColumns(columns);

  // At 2-up the board matches the app's standard condensed content width, so we
  // hand off to the shared Layout.Container (same centering as the rest of the
  // product) instead of our own max-width band. The wider
  // 3/4-up boards stay full-bleed with a bespoke centered column.
  const useStandardContainer = columns === 2;
  const containerWidthClass = useStandardContainer ? undefined : COLUMN_CONTAINER_CLASS[columns];

  const {slugs: pinnedAppSlugs, pinApp, unpinApp} = usePinnedApps(employee?.user_id);
  // Each pinned app is its own embedded tile, newest first, ahead of built-in tiles.
  const pinnedAppTileIds = useMemo(
    () => (isAppBuilderPlatformEnabled ? pinnedAppSlugs.map(toAppTileId) : []),
    [isAppBuilderPlatformEnabled, pinnedAppSlugs]
  );

  const onAddApp = useCallback(
    (slug: string) => {
      pinApp(slug);
      setSnackbar({text: t('dashboard_apps_tile_added', {name: displayName(slug)})});
    },
    [pinApp, setSnackbar]
  );
  useAddAppFromUrl({
    enabled: isAppBuilderPlatformEnabled && Boolean(employee?.user_id),
    onAdd: onAddApp,
  });

  // Removing an app tile unpins it rather than hiding it, so re-pinning brings it back.
  const removeTile = useCallback(
    (tileId: TileId) => {
      const appSlug = appSlugFromTileId(tileId);
      if (appSlug) {
        logDashboardInteraction({action: 'tile_remove', tileId});
        unpinApp(appSlug);
      } else {
        hideTile(tileId);
      }
    },
    [hideTile, unpinApp]
  );

  const liveCtx = useMemo(() => ({hasTeam}), [hasTeam]);

  const hiddenSet = useMemo(() => new Set(config.hidden), [config.hidden]);
  const layoutPinned = useMemo(() => {
    const pinned = new Set(config.added);
    config.order.forEach((id) => pinned.add(id));
    return pinned;
  }, [config.added, config.order]);

  const liveTileStatuses = useMemo(() => buildLiveTileStatuses(state, liveCtx), [state, liveCtx]);

  useEffect(() => {
    recordStatuses(liveTileStatuses);
  }, [liveTileStatuses, recordStatuses]);

  const liveVisibility = useMemo(
    () => ({
      remembered,
      isFirstEverLoad,
      userId: employee?.user_id,
      hidden: hiddenSet,
      layoutPinned,
    }),
    [remembered, isFirstEverLoad, employee?.user_id, hiddenSet, layoutPinned]
  );

  const defaultLiveOrder = useMemo(
    () => [...pinnedAppTileIds, ...getVisibleLiveTilesInOrder(state, liveCtx, liveVisibility)],
    [pinnedAppTileIds, state, liveCtx, liveVisibility]
  );

  // Flag-gated tiles drop out even if the viewer added them while the flag was on.
  const resolvedLayout = useMemo(() => {
    const layout = resolveLayout(defaultLiveOrder, config);
    return {...layout, order: layout.order.filter((id) => isTileFeatureEnabled(id, flags))};
  }, [defaultLiveOrder, config, flags]);

  const rootRef = useRef<HTMLDivElement>(null);
  const [visitId] = useState(newHomeVisitId);
  const tileImpressions = useHomeVisitTracking({
    rootRef,
    visitId,
    order: resolvedLayout.order,
    statuses: liveTileStatuses,
    editing,
    effectiveColumns,
  });
  useHomeAgendaLoadedLog({
    visitId,
    state,
    tileShown: resolvedLayout.order.includes('agenda'),
  });

  useEffect(() => {
    logHomeSurfaceViewed({
      surface: 'mission_control',
      visitId,
      customizeEnabled,
      tileCount: resolvedLayout.order.length,
      isCustomized: resolvedLayout.isCustomized,
      columns: userColumns,
      ...(fromPeople ? {via: 'people' as const} : {}),
      ...readHomeViewport(effectiveColumns),
    });
    // One impression per dashboard visit; layout may still be settling on first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  return (
    <Layout.Container width={useStandardContainer ? 'default' : '100%'}>
      <Box ref={rootRef} width="100%" style={{margin: '0 auto'}} className={containerWidthClass}>
        <HomeVisitContext.Provider value={tileImpressions}>
          <Stack gap="8">
            <Banner />
            <Box paddingY="12">
              <DashboardHeader state={state} firstName={firstName} />
            </Box>
            <StickySearchBar sticky={!editing}>
              <SearchSection source={fromPeople ? 'home_directory' : 'home'} />
            </StickySearchBar>
            <SavedAiViewsSection />
            <Stack gap="16">
              {editing && (
                <DashboardCustomizeBar
                  currentTileIds={new Set(resolvedLayout.order)}
                  isCustomized={resolvedLayout.isCustomized}
                  userId={employee?.user_id}
                  columns={userColumns}
                  onDone={() => {
                    logDashboardInteraction({action: 'customize_close'});
                    setEditing(false);
                  }}
                  onReset={resetLayout}
                  onAddTile={addTile}
                  onSetColumns={setColumns}
                />
              )}

              {resolvedLayout.order.length === 0 ? (
                <DashboardEmptyState
                  onCustomize={
                    customizeEnabled
                      ? () => {
                          logDashboardInteraction({action: 'customize_open'});
                          setEditing(true);
                        }
                      : undefined
                  }
                />
              ) : (
                <DashboardGrid
                  tileOrder={resolvedLayout.order}
                  tileSizes={resolvedLayout.sizes}
                  editing={editing}
                  onSetOrder={setOrder}
                  onRemoveTile={removeTile}
                  onSetTileSize={setTileSize}
                  state={state}
                  columns={columns}
                />
              )}
            </Stack>
          </Stack>
        </HomeVisitContext.Provider>
      </Box>
    </Layout.Container>
  );
};
